import { Stack, Text } from '@atlaskit/primitives';
import {
  type ActionMeta,
  CheckboxSelect,
  components,
  type MultiValueProps,
  type SelectComponentsConfig,
  type StylesConfig,
  type ValueContainerProps
} from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { Children, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

export type MultiSelectOption<T extends string = string> = {
  label: string;
  value: T;
};

interface MultiSelectInputProps<T extends string = string> {
  inputId: string;
  options: MultiSelectOption<T>[];
  value: MultiSelectOption<T>[];
  onChange: (value: MultiSelectOption<T>[]) => void;
  label?: ReactNode;
  placeholder?: string;
  helpText?: ReactNode;
  errorMessage?: ReactNode;
  isDisabled?: boolean;
  isRequired?: boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
  spacing?: 'compact' | 'default';
  enableSelectAll?: boolean;
  selectAllLabel?: string;
  name?: string;
  testId?: string;
  ariaLabel?: string;
  menuPortalTarget?: HTMLElement | null;
}

const CONTROL_RESERVED_WIDTH = 88;
const CHIP_HORIZONTAL_PADDING = 52;
const BADGE_HORIZONTAL_PADDING = 28;
const CHIP_FONT =
  '500 14px ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const SELECT_ALL_VALUE = '__all__';

const textMeasureCanvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;

const measureTextWidth = (text: string) => {
  const context = textMeasureCanvas?.getContext('2d');
  if (!context) {
    return text.length * 8;
  }

  context.font = CHIP_FONT;
  return context.measureText(text).width;
};

const estimateChipWidth = (label: string) => {
  return Math.min(measureTextWidth(label) + CHIP_HORIZONTAL_PADDING, 180);
};

const estimateBadgeWidth = (hiddenCount: number) => {
  return measureTextWidth(`+${hiddenCount}`) + BADGE_HORIZONTAL_PADDING;
};

const getVisibleValueCount = <T extends string>(
  selectedOptions: MultiSelectOption<T>[],
  containerWidth: number
) => {
  if (selectedOptions.length === 0 || containerWidth <= 0) {
    return selectedOptions.length;
  }

  const availableWidth = Math.max(containerWidth - CONTROL_RESERVED_WIDTH, 0);
  let usedWidth = 0;
  let visibleCount = 0;

  for (let index = 0; index < selectedOptions.length; index += 1) {
    const nextOption = selectedOptions[index];

    if (!nextOption) {
      continue;
    }

    const remainingCount = selectedOptions.length - index - 1;
    const chipWidth = estimateChipWidth(nextOption.label);
    const badgeWidth = remainingCount > 0 ? estimateBadgeWidth(remainingCount) : 0;

    if (usedWidth + chipWidth + badgeWidth > availableWidth) {
      break;
    }

    usedWidth += chipWidth;
    visibleCount += 1;
  }

  return visibleCount;
};

export function MultiSelectInput<T extends string = string>({
  inputId,
  options,
  value,
  onChange,
  label,
  placeholder,
  helpText,
  errorMessage,
  isDisabled = false,
  isRequired = false,
  isClearable = true,
  isSearchable = true,
  spacing = 'default',
  enableSelectAll = false,
  selectAllLabel = 'Select all',
  name,
  testId,
  ariaLabel,
  menuPortalTarget
}: MultiSelectInputProps<T>) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const describedBy = [
    helpText ? `${inputId}-help` : null,
    errorMessage ? `${inputId}-error` : null
  ]
    .filter(Boolean)
    .join(' ');
  const visibleValueCount = getVisibleValueCount(value, containerWidth);
  const hiddenValueCount = Math.max(value.length - visibleValueCount, 0);
  const selectedValues = useMemo(() => new Set(value.map((option) => option.value)), [value]);
  const sortedOptions = useMemo(() => {
    const selectedOptions = options.filter((option) => selectedValues.has(option.value));
    const unselectedOptions = options.filter((option) => !selectedValues.has(option.value));

    return [...selectedOptions, ...unselectedOptions];
  }, [options, selectedValues]);
  const allOptionsSelected = options.length > 0 && value.length === options.length;
  const selectAllOption = useMemo(
    () =>
      ({
        label: selectAllLabel,
        value: SELECT_ALL_VALUE as T
      }) satisfies MultiSelectOption<T>,
    [selectAllLabel]
  );
  const resolvedOptions = enableSelectAll ? [selectAllOption, ...sortedOptions] : sortedOptions;

  useEffect(() => {
    const element = containerRef.current;

    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }

    const updateWidth = () => {
      setContainerWidth(element.getBoundingClientRect().width);
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const selectComponents: SelectComponentsConfig<MultiSelectOption<T>, true> = {
    MultiValue: (props: MultiValueProps<MultiSelectOption<T>, true>) => {
      if (props.index >= visibleValueCount) {
        return null;
      }

      return <components.MultiValue {...props} />;
    },
    ValueContainer: (props: ValueContainerProps<MultiSelectOption<T>, true>) => {
      if (value.length === 0) {
        return <components.ValueContainer {...props}>{props.children}</components.ValueContainer>;
      }

      const children = Children.toArray(props.children);
      const inputChild = children.at(-1);
      const valueChildren = children.slice(0, -1).slice(0, visibleValueCount);

      return (
        <components.ValueContainer {...props}>
          {valueChildren}
          {hiddenValueCount > 0 ? (
            <span
              style={{
                alignItems: 'center',
                backgroundColor: token('color.background.neutral.subtle'),
                borderRadius: '999px',
                color: token('color.text.subtle'),
                display: 'inline-flex',
                flexShrink: 0,
                fontSize: '0.75rem',
                fontWeight: 600,
                lineHeight: 1,
                marginInlineEnd: token('space.050'),
                padding: `${token('space.025')} ${token('space.075')}`,
                whiteSpace: 'nowrap'
              }}
            >
              +{hiddenValueCount}
            </span>
          ) : null}
          {inputChild}
        </components.ValueContainer>
      );
    }
  };

  const styles: StylesConfig<MultiSelectOption<T>, true> = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? token('color.border.focused') : base.borderColor,
      boxShadow: state.isFocused ? `0 0 0 1px ${token('color.border.focused')}` : base.boxShadow,
      '&:hover': {
        ...base['&:hover'],
        borderColor: state.isFocused ? token('color.border.focused') : base.borderColor
      }
    }),
    valueContainer: (base) => ({
      ...base,
      flexWrap: 'nowrap',
      overflow: 'hidden'
    }),
    multiValue: (base) => ({
      ...base,
      flexShrink: 0,
      maxWidth: 'none'
    }),
    placeholder: (base) => ({
      ...base,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999
    })
  };

  return (
    <Stack space="space.050">
      {label ? (
        <label
          htmlFor={inputId}
          style={{
            color: token('color.text'),
            display: 'inline-block',
            fontSize: '0.875rem',
            fontWeight: 500
          }}
        >
          {label}
        </label>
      ) : null}

      <div ref={containerRef} style={{ width: '100%' }}>
        <CheckboxSelect<MultiSelectOption<T>>
          inputId={inputId}
          name={name}
          testId={testId}
          options={resolvedOptions}
          value={value}
          onChange={(nextValue, actionMeta?: ActionMeta<MultiSelectOption<T>>) => {
            const normalizedValue = Array.isArray(nextValue)
              ? nextValue.filter((option) => option.value !== SELECT_ALL_VALUE)
              : [];

            if (enableSelectAll && actionMeta?.option?.value === SELECT_ALL_VALUE) {
              onChange(allOptionsSelected ? [] : options);
              return;
            }

            onChange(normalizedValue);
          }}
          placeholder={placeholder}
          isDisabled={isDisabled}
          isRequired={isRequired}
          isClearable={isClearable}
          isSearchable={isSearchable}
          menuPortalTarget={menuPortalTarget ?? (typeof document !== 'undefined' ? document.body : null)}
          spacing={spacing}
          components={selectComponents}
          styles={styles}
          menuPosition={menuPortalTarget ? 'fixed' : 'absolute'}
          aria-label={ariaLabel ?? (typeof label === 'string' ? label : undefined)}
          aria-describedby={describedBy || undefined}
          hideSelectedOptions={false}
          isOptionSelected={(option) => {
            if (enableSelectAll && option.value === SELECT_ALL_VALUE) {
              return allOptionsSelected;
            }

            return selectedValues.has(option.value);
          }}
        />
      </div>

      {helpText ? (
        <Text id={`${inputId}-help`} size="small" color="color.text.subtle">
          {helpText}
        </Text>
      ) : null}

      {errorMessage ? (
        <Text id={`${inputId}-error`} size="small" color="color.text.danger">
          {errorMessage}
        </Text>
      ) : null}
    </Stack>
  );
}

export default MultiSelectInput;
